import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/auth/auth";
import { getEnumMemberName } from "@/lib/enum-utils";
import { useInvitationAcceptInvitation, useInvitationValidateInvitation } from "@/orval/invitation";
import { OrganizationRoleType } from "@/orval/model";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useMemo } from "react";
import { z } from "zod";
import { parseISO } from "date-fns/parseISO";
import { format } from "date-fns";
import { Building2Icon, CalendarClockIcon, MailIcon } from "lucide-react";

const invitationAcceptSearchSchema = z.object({
  token: z.string().optional(),
});

const InvitationAcceptPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { token } = Route.useSearch();

  const { data: validationResult, isLoading: isValidating } = useInvitationValidateInvitation(
    { token },
    {
      query: {
        enabled: !!token,
      },
    },
  );

  const { mutateAsync: acceptInvitation, isPending: isAccepting } = useInvitationAcceptInvitation();

  const invitation = validationResult?.invitation;
  const isValid = validationResult?.isValid ?? false;
  const failureReason = validationResult?.failureReason;

  const currentUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    return window.location.href;
  }, []);

  const handleAccept = async () => {
    if (!token) {
      return;
    }

    try {
      await acceptInvitation({ data: { token } });
      toast.success("Invitation accepted.");
      navigate({ to: "/organizations" });
    } catch (error) {
      const failure = String(
        (error as { response?: { data?: { failureReason?: string } } })?.response?.data?.failureReason ?? "",
      );

      if (failure === "EmailMismatch") {
        toast.error("Signed-in account does not match the invited email. Please switch accounts.");
        navigate({
          to: "/login",
          search: {
            redirect: currentUrl,
            message: "Please sign in with the invited email address.",
          },
        });
        return;
      }

      toast.error("Unable to accept invitation.");
    }
  };

  if (!token) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Invitation link is missing</CardTitle>
            <CardDescription>The invitation token was not found in the URL.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/login" search={{ redirect: undefined, message: undefined }}>
              <Button>Go to Login</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Validating invitation...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!isValid || !invitation) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Invitation is not valid</CardTitle>
            <CardDescription>
              {failureReason
                ? `Reason: ${failureReason}`
                : "This invitation may have expired, been revoked, or already been used."}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/login" search={{ redirect: undefined, message: undefined }}>
              <Button variant="outline">Go to Login</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center bg-muted/20 p-4 md:p-8">
      <Card className="w-full max-w-2xl border-border/60 shadow-sm">
        <CardHeader className="gap-3">
          <Badge variant="outline" className="w-fit">
            Invitation
          </Badge>
          <CardTitle>Join {invitation.organizationName}</CardTitle>
          <CardDescription>
            You have been invited to join this organization as a{" "}
            {getEnumMemberName(OrganizationRoleType, invitation.targetRoleType)}.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border/60 bg-background p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MailIcon className="size-4" />
                Invited Email
              </div>
              <p className="break-all text-sm font-medium text-foreground">{invitation.email}</p>
            </div>

            <div className="rounded-lg border border-border/60 bg-background p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Building2Icon className="size-4" />
                Role
              </div>
              <p className="text-sm font-medium text-foreground">
                {getEnumMemberName(OrganizationRoleType, invitation.targetRoleType)}
              </p>
            </div>

            <div className="rounded-lg border border-border/60 bg-background p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CalendarClockIcon className="size-4" />
                Expires
              </div>
              <p className="text-sm font-medium text-foreground">{format(parseISO(invitation.expiresAtUtc), "PPpp")}</p>
            </div>
          </div>

          <div className="rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
            Accepting this invitation will connect your account to {invitation.organizationName} with the invited role.
            If you need to use a different email, sign in or create an account with the invited address first.
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 md:flex-row md:justify-end">
          {!isAuthenticated ? (
            <>
              <Link to="/sign-up" search={{ redirect: currentUrl }}>
                <Button variant="outline">Create Account</Button>
              </Link>
              <Link
                to="/login"
                search={{ redirect: currentUrl, message: "Please sign in with the invited email address." }}
              >
                <Button>Sign In to Accept</Button>
              </Link>
            </>
          ) : (
            <Button onClick={handleAccept} disabled={isAccepting}>
              Accept Invitation
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/invitations/accept")({
  validateSearch: invitationAcceptSearchSchema,
  component: InvitationAcceptPage,
});
